import React, { useState } from "react";
import { Mail, Copy, Check, Heart } from "lucide-react";
import { contactLinks } from "../data/mock";
import { logWarn } from "../lib/logger";

// ---------------------------------------------------------------------------
// Clipboard fallback — silently degrades when the secure clipboard API is
// unavailable (e.g. older Safari, http contexts).
// ---------------------------------------------------------------------------
const copyTextLegacy = (text) => {
  const el = document.createElement("textarea");
  el.value = text;
  el.setAttribute("readonly", "");
  el.style.position = "fixed";
  el.style.opacity = "0";
  document.body.appendChild(el);
  el.select();
  document.execCommand("copy");
  document.body.removeChild(el);
};

const copyText = async (text) => {
  if (navigator.clipboard && window.isSecureContext) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch (err) {
      logWarn("Clipboard API failed, using legacy fallback:", err);
      // fall through to legacy path
    }
  }
  try {
    copyTextLegacy(text);
    return true;
  } catch (err) {
    logWarn("Legacy clipboard copy failed:", err);
    return false;
  }
};

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------
const AvailabilityBadge = () => (
  <div className="flex justify-center">
    <div className="inline-flex items-center gap-2.5 px-4 py-2 rounded-full border border-emerald-400/25 bg-emerald-500/[0.08] text-emerald-300">
      <span className="relative flex h-1.5 w-1.5">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-70" />
        <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-400" />
      </span>
      <span className="text-[12px] font-medium uppercase tracking-[0.18em]">
        Available for new adventures
      </span>
    </div>
  </div>
);

const Headline = () => (
  <h2 className="mt-10 text-[44px] sm:text-[64px] lg:text-[88px] leading-[1.0] tracking-[-0.04em] font-semibold">
    Let&apos;s build something
    <br />
    <span className="bg-clip-text text-transparent bg-gradient-to-r from-sky-300 via-violet-300 to-emerald-300">
      meaningful
    </span>{" "}
    together.
  </h2>
);

const GetInTouchButton = ({ email }) => (
  <div className="mt-14 flex justify-center">
    <a
      href={`mailto:${email}`}
      className="group inline-flex items-center gap-3 px-10 lg:px-12 py-5 lg:py-6 rounded-3xl bg-sky-300 hover:bg-sky-200 text-neutral-950 text-[18px] lg:text-[20px] font-semibold tracking-tight shadow-[0_20px_60px_-20px_rgba(125,211,252,0.45)] transition-colors duration-200"
    >
      <Mail className="w-5 h-5 lg:w-6 lg:h-6" strokeWidth={2.25} />
      Get In Touch
    </a>
  </div>
);

const EmailPill = ({ email, copied, onCopy }) => (
  <div className="mt-6 flex flex-col items-center gap-2">
    <button
      type="button"
      onClick={onCopy}
      aria-label="Copy email address"
      data-testid="contact-copy-email-button"
      className="group inline-flex items-center justify-between gap-4 w-full max-w-[420px] px-5 py-3.5 rounded-2xl border border-white/10 bg-white/[0.03] hover:bg-white/[0.06] hover:border-white/20 transition-colors duration-200"
    >
      <span className="text-[14.5px] text-neutral-300 truncate">{email}</span>
      <span className="shrink-0 text-neutral-400 group-hover:text-neutral-200 transition-colors duration-200">
        {copied ? (
          <Check className="w-4 h-4" strokeWidth={2.25} />
        ) : (
          <Copy className="w-4 h-4" strokeWidth={2} />
        )}
      </span>
    </button>

    <div
      aria-live="polite"
      data-testid="contact-copied-toast"
      className={`pointer-events-none inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/15 border border-emerald-400/30 text-emerald-300 text-[12px] font-medium transition-all duration-200 ${
        copied ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-1"
      }`}
    >
      <Check className="w-3 h-3" strokeWidth={2.5} />
      Copied!
    </div>
  </div>
);

const LinkedInLink = ({ href }) => (
  <div className="mt-12">
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="inline-block text-[15px] font-medium text-sky-300 hover:text-sky-200 transition-colors duration-200 border-b-2 border-sky-400/60 hover:border-sky-300 pb-1"
    >
      My LinkedIn
    </a>
  </div>
);

const Copyright = () => (
  <div className="relative border-t border-white/10">
    <div className="max-w-[1200px] mx-auto px-6 lg:px-10 py-7 flex flex-col sm:flex-row sm:items-center sm:justify-center gap-2 text-center">
      <p className="text-[13px] text-neutral-500 inline-flex items-center justify-center gap-1.5 flex-wrap">
        <span>
          © {new Date().getFullYear()} Shweta Chauhan. Designed with love and intention
        </span>
        <Heart className="w-3.5 h-3.5 text-rose-400 fill-rose-400 inline-block" strokeWidth={1.5} />
      </p>
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// HiringCTA
// ---------------------------------------------------------------------------
const HiringCTA = () => {
  const [copied, setCopied] = useState(false);

  const onCopyEmail = async () => {
    const ok = await copyText(contactLinks.email);
    if (!ok) return;
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  return (
    <section
      id="contact"
      className="relative bg-neutral-950 text-neutral-50 overflow-hidden"
    >
      {/* Subtle dot grid + glow */}
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_1px_1px,rgba(255,255,255,0.05)_1px,transparent_0)] [background-size:28px_28px] [mask-image:radial-gradient(ellipse_70%_60%_at_50%_30%,#000_30%,transparent_80%)]" />
      <div className="pointer-events-none absolute -top-32 left-1/2 -translate-x-1/2 w-[900px] h-[420px] rounded-full bg-white/[0.04] blur-3xl" />

      <div className="relative max-w-[1100px] mx-auto px-6 lg:px-10 py-24 lg:py-32 text-center">
        <AvailabilityBadge />
        <Headline />
        <p className="mt-7 text-[16px] sm:text-[18px] text-neutral-400 max-w-[640px] mx-auto leading-[1.6]">
          If you&apos;ve got an idea in mind, I&apos;m all ears.
        </p>
        <GetInTouchButton email={contactLinks.email} />
        <EmailPill email={contactLinks.email} copied={copied} onCopy={onCopyEmail} />
        <LinkedInLink href={contactLinks.linkedin} />
      </div>

      <Copyright />
    </section>
  );
};

export default HiringCTA;
